// CustomerManagerDashboard.tsx
import React, { useState, useEffect } from "react";
import {
  Megaphone,
  CalendarDays,
  Package,
  Image as ImageIcon,
  Clock,
  CheckCircle2,
  TrendingUp,
  ChevronRight,
  PieChart,
  Activity,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/store";
import { API_V1_BASE_URL } from "../../config/api";

// ---- Types -----------------------------------------------------------

interface CampaignStats {
  total: number;
  active: number;
  archived: number;
}

interface EventStats {
  total: number;
  upcoming: number;
  past: number;
}

interface ProductStats {
  total: number;
  active: number;
  inactive: number;
}

interface AssetStats {
  total: number;
  ready: number;
  processing: number;
}

interface MonthlyGrowthPoint {
  month: string;
  count: number;
}

interface DashboardStatsResponse {
  campaigns: CampaignStats;
  events: EventStats;
  products: ProductStats;
  assets: AssetStats;
  campaignGrowth: MonthlyGrowthPoint[];
}

interface Campaign {
  campaign_id: string;
  title: string;
  status: string;
  start_date?: string;
  end_date?: string;
  updated_at?: string;
}

// ---- Component ---------------------------------------------------------

const CustomerManagerDashboard: React.FC = () => {
  const { token, accessToken, user } = useSelector((state: RootState) => state.auth);
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const authToken = accessToken || token || localStorage.getItem("accessToken");
  const tenantId =
    (user as any)?.tenantId ||
    (user as any)?.tenant_id ||
    storedUser?.tenantId ||
    storedUser?.tenant_id;

  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [recentCampaigns, setRecentCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);

  const authHeaders = {
    Authorization: `Bearer ${authToken || ""}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!authToken) {
          throw new Error("No authentication token found. Please login again.");
        }
        if (!tenantId) {
          throw new Error("No tenant found for this account.");
        }

        const statsRes = await fetch(
          `${API_V1_BASE_URL}/customer-manager/dashboard-stats?tenant_id=${tenantId}`,
          { headers: authHeaders },
        );

        if (!statsRes.ok) {
          const errData = await statsRes.json().catch(() => null);
          throw new Error(errData?.message || "Failed to fetch dashboard stats");
        }
        const statsJson = await statsRes.json();
        setStats(statsJson.data as DashboardStatsResponse);

        // Recent campaigns are supplementary. A failure here must not prevent
        // the manager dashboard statistics from rendering.
        try {
          const campaignsRes = await fetch(
            `${API_V1_BASE_URL}/campaigns?tenant_id=${tenantId}&page=1&limit=5`,
            { headers: authHeaders },
          );
          if (campaignsRes.ok) {
            const campaignsJson = await campaignsRes.json();
            setRecentCampaigns(
              Array.isArray(campaignsJson.data) ? campaignsJson.data : [],
            );
          } else {
            setRecentCampaigns([]);
          }
        } catch (campaignError) {
          console.warn("Unable to load recent campaigns:", campaignError);
          setRecentCampaigns([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        console.error("Error fetching customer manager dashboard:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [authToken, tenantId]);

  const pct = (part: number, total: number) =>
    total > 0 ? Math.round((part / total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <div className="text-red-500 text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <p className="text-xl font-semibold">Error Loading Dashboard</p>
            <p className="mt-2 text-gray-600">{error || "No data available"}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { campaigns, events, products, assets, campaignGrowth } = stats;
  const maxGrowth = Math.max(...campaignGrowth.map((d) => d.count), 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-6">
      {/* Header */}
      <div className="mb-8 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
              Dashboard Overview
            </h1>
            <p className="text-gray-500 mt-1 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              A summary of campaigns, events, products and assets for your
              tenant
            </p>
          </div>
          <span className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Live
          </span>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<Megaphone className="w-6 h-6 text-blue-500" />}
          title="Campaigns"
          value={campaigns.total}
          subtitle={`${campaigns.active} active`}
          color="blue"
        />
        <StatCard
          icon={<CalendarDays className="w-6 h-6 text-purple-500" />}
          title="Events"
          value={events.total}
          subtitle={`${events.upcoming} upcoming`}
          color="purple"
        />
        <StatCard
          icon={<Package className="w-6 h-6 text-green-500" />}
          title="Products"
          value={products.total}
          subtitle={`${products.active} active`}
          color="green"
        />
        <StatCard
          icon={<ImageIcon className="w-6 h-6 text-yellow-500" />}
          title="Assets"
          value={assets.total}
          subtitle="Total assets"
          color="yellow"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Campaign growth */}
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Campaign Growth
              </h3>
              <p className="text-sm text-gray-500">
                Campaigns created over the last 7 months
              </p>
            </div>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <div className="relative h-64">
            {campaignGrowth.length > 0 ? (
              <svg className="w-full h-full" viewBox="0 0 600 250" preserveAspectRatio="none">
                {[0, 25, 50, 75, 100].map((v) => (
                  <line
                    key={v}
                    x1="40"
                    y1={200 - v * 1.8}
                    x2="580"
                    y2={200 - v * 1.8}
                    stroke="#E5E7EB"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                    opacity="0.5"
                  />
                ))}

                <polyline
                  points={campaignGrowth
                    .map((d, i) => {
                      const x = 40 + (i / Math.max(campaignGrowth.length - 1, 1)) * 540;
                      const y = 200 - (d.count / maxGrowth) * 190;
                      return `${x},${y}`;
                    })
                    .join(" ")}
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {campaignGrowth.map((d, i) => {
                  const x = 40 + (i / Math.max(campaignGrowth.length - 1, 1)) * 540;
                  const y = 200 - (d.count / maxGrowth) * 190;
                  return (
                    <g key={i}>
                      <circle
                        cx={x}
                        cy={y}
                        r={hoveredPoint === i ? 8 : 5}
                        fill="#3B82F6"
                        stroke="white"
                        strokeWidth="3"
                        className="transition-all duration-200 cursor-pointer"
                        onMouseEnter={() => setHoveredPoint(i)}
                        onMouseLeave={() => setHoveredPoint(null)}
                      />
                      {hoveredPoint === i && (
                        <>
                          <rect x={x - 20} y={y - 35} width="40" height="24" rx="6" fill="white" stroke="#E5E7EB" />
                          <text x={x} y={y - 18} textAnchor="middle" className="text-xs font-semibold fill-gray-700">
                            {d.count}
                          </text>
                        </>
                      )}
                      <text x={x} y="220" textAnchor="middle" className="text-xs fill-gray-600 font-medium">
                        {d.month}
                      </text>
                    </g>
                  );
                })}
              </svg>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-400">No data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Campaign status breakdown */}
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Campaign Status</h3>
              <p className="text-sm text-gray-500">Active and archived</p>
            </div>
            <PieChart className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            <StatusBar label="Active" value={campaigns.active} total={campaigns.total} color="#3B82F6" />
            <StatusBar label="Archived" value={campaigns.archived} total={campaigns.total} color="#6B7280" />
          </div>
        </div>
      </div>

      {/* Products / Assets / Events row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Products</h3>
            <Package className="w-5 h-5 text-gray-400" />
          </div>
          <StatusBar label="Active" value={products.active} total={products.total} color="#10B981" />
          <div className="mt-3">
            <StatusBar label="Inactive" value={products.inactive} total={products.total} color="#D1D5DB" />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Assets</h3>
            <ImageIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="flex items-center justify-center py-4">
            <div className="text-center">
              <p className="text-4xl font-bold text-gray-900">{assets.total}</p>
              <p className="text-sm text-gray-500 mt-1">Total assets</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Events</h3>
            <CalendarDays className="w-5 h-5 text-gray-400" />
          </div>
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-sm text-gray-600">Upcoming</span>
            <span className="ml-auto text-sm font-semibold text-gray-900">{events.upcoming}</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">Past</span>
            <span className="ml-auto text-sm font-semibold text-gray-900">{events.past}</span>
          </div>
        </div>
      </div>

      {/* Recent campaigns */}
      <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Recent Campaigns</h3>
            <p className="text-sm text-gray-500">Latest updates for your tenant</p>
          </div>
          <Megaphone className="w-5 h-5 text-gray-400" />
        </div>
        <div className="space-y-3">
          {recentCampaigns.length === 0 && (
            <p className="text-gray-400 text-sm">No campaigns yet.</p>
          )}
          {recentCampaigns.map((c) => (
            <div
              key={c.campaign_id}
              className="group flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-all duration-200 cursor-pointer"
            >
              <div>
                <p className="text-sm font-medium text-gray-800 group-hover:text-blue-600 transition-colors">
                  {c.title}
                </p>
                <p className="text-xs text-gray-400">
                  {c.start_date ? new Date(c.start_date).toLocaleDateString() : ""}
                  {c.end_date ? ` – ${new Date(c.end_date).toLocaleDateString()}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600 capitalize">
                  {c.status}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---- Shared small components --------------------------------------------

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: number;
  subtitle: string;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, subtitle, color }) => {
  const colorClasses: Record<string, string> = {
    blue: "border-blue-500 hover:border-blue-600",
    green: "border-green-500 hover:border-green-600",
    purple: "border-purple-500 hover:border-purple-600",
    yellow: "border-yellow-500 hover:border-yellow-600",
  };

  return (
    <div
      className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border-l-4 ${colorClasses[color]} hover:scale-[1.02] group`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1 group-hover:scale-105 transition-transform origin-left">
            {value}
          </p>
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl group-hover:scale-110 transition-transform duration-300 group-hover:shadow-lg">
          {icon}
        </div>
      </div>
    </div>
  );
};

interface StatusBarProps {
  label: string;
  value: number;
  total: number;
  color: string;
}

const StatusBar: React.FC<StatusBarProps> = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-500 font-medium">{value}</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div
          className="h-2.5 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: color, boxShadow: `0 0 10px ${color}40` }}
        ></div>
      </div>
    </div>
  );
};

export default CustomerManagerDashboard;
